import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Soft-fades the bottom edge of a scroll container while there is more content
 * below, and clears the fade the moment you reach the end.
 *
 * WHY THIS EXISTS
 * ---------------
 * The treatment was hand-rolled in four places — the filter facet list, the
 * address-icon grid, the activity timeline, and the tag shop — each with its own
 * copy of the same `linear-gradient` mask and, in three cases, its own copy of
 * the scroll/resize bookkeeping that decides when to show it. The fourth
 * (Activity) never wrote that bookkeeping, so its fade was PERMANENT: rows stayed
 * dissolved even when you had scrolled to the last one, which reads as "there's
 * more" forever and makes the final row look clipped rather than final.
 *
 * That split is the argument for extracting it. The interesting part of this
 * pattern is not the gradient, it is knowing when to stop drawing it — so a
 * shared gradient constant would have fixed nothing. The bookkeeping is what
 * needed to be shared.
 *
 * WHAT IT WATCHES
 * ---------------
 * Overflow can start or stop for reasons that are not scrolling: the container
 * resizes, or its contents change (a filter narrows a list, a page of results
 * loads, an accordion opens). So this listens to `scroll`, and observes BOTH the
 * container and its children via ResizeObserver, rather than assuming a scroll
 * event will always follow a change.
 *
 * USAGE
 *
 *   const { ref, fadeSx } = useScrollFade()
 *   <Box ref={ref} sx={{ overflowY: 'auto', ...fadeSx }}>…</Box>
 *
 * Pass `edges: 'both'` to fade the TOP edge as well once the box has been scrolled
 * away from the start — see the option's note for why that is opt-in.
 */
export interface UseScrollFadeOptions {
  /** Height of the fade in px. Bigger containers usually want a taller fade. */
  fadeHeight?: number;
  /** Set false to disable entirely (e.g. a mode where the box doesn't scroll). */
  enabled?: boolean;
  /**
   * Which edges fade. Default `'bottom'` — the original behaviour, unchanged for
   * every existing caller.
   *
   * `'both'` is OPT-IN rather than the default because the two edges do not carry
   * the same information. The bottom fade is a DISCOVERY affordance: it tells you
   * about content you have not seen. The top fade is not — you scrolled past that
   * content, so you already know it is there — its job is purely to stop the
   * content clipping against a hard edge. That asymmetry means "both" is a
   * judgement about the surface (does its top edge butt against a visible
   * boundary? is the fade tall enough to dim a lot of read content?), not a
   * strictly-better default worth changing three shipped surfaces to get.
   */
  edges?: "bottom" | "top" | "both";
}

const NO_FADE = { top: false, bottom: false } as const;

export const useScrollFade = <T extends HTMLElement = HTMLDivElement>({
  fadeHeight = 40,
  enabled = true,
  edges = "bottom",
}: UseScrollFadeOptions = {}) => {
  // Generic so callers get a ref typed to the element they actually attach it to
  // (MUI's Box/Stack refs are element-specific); defaults to HTMLDivElement,
  // which covers every current call site.
  const ref = useRef<T | null>(null);
  const [fade, setFade] = useState<{ top: boolean; bottom: boolean }>(NO_FADE);

  const fadeTop = edges === "top" || edges === "both";
  const fadeBottom = edges === "bottom" || edges === "both";

  const measure = useCallback(() => {
    const el = ref.current;
    // Identity-stable no-op when nothing changed: `scroll` fires at frame rate, and
    // this used to set a boolean (which React bails out of via Object.is). A fresh
    // object per event would re-render the whole subtree on every scroll frame, so
    // the comparison has to be done here.
    const next = (prev: { top: boolean; bottom: boolean }) => {
      if (!el || !enabled) return prev === NO_FADE ? prev : NO_FADE;
      // 1px tolerance: fractional scroll offsets and zoom mean
      // scrollTop + clientHeight rarely equals scrollHeight exactly at the true
      // bottom, so a strict comparison leaves a sliver of fade permanently on.
      const top = fadeTop && el.scrollTop > 1;
      const bottom =
        fadeBottom && el.scrollHeight - el.scrollTop - el.clientHeight > 1;
      return prev.top === top && prev.bottom === bottom ? prev : { top, bottom };
    };
    setFade(next);
  }, [enabled, fadeTop, fadeBottom]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    measure();
    el.addEventListener("scroll", measure, { passive: true });

    // Observe the CHILDREN too, not just the container: when the container has a
    // fixed height, its own box never changes as content grows or shrinks, so
    // observing it alone misses exactly the case that flips overflow on and off.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    Array.from(el.children).forEach((child) => ro.observe(child));

    // Children can be added or removed without any resize firing at all.
    const mo = new MutationObserver(() => {
      measure();
      Array.from(el.children).forEach((child) => ro.observe(child));
    });
    mo.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
      mo.disconnect();
    };
  }, [measure]);

  // Fonts and images settle after first paint and can change whether the content
  // overflows; re-measure once the browser is idle rather than trusting layout
  // as of mount.
  useEffect(() => {
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [measure]);

  // ONE mask with up to two stop pairs, never two competing mask-images: a second
  // `mask-image` would replace the first, so mid-scroll (both edges active) only
  // one of them could ever be drawn. A gradient holds its last stop's colour, so
  // the top-only form stays opaque all the way down without a closing stop.
  //
  // At a container height below 2 * fadeHeight the two inner stops cross; CSS
  // clamps stop positions to be non-decreasing, so that degrades to a hard split
  // rather than an inverted mask. Every current caller's box is far taller.
  const stops = [
    fade.top ? `transparent 0, #000 ${fadeHeight}px` : null,
    fade.bottom ? `#000 calc(100% - ${fadeHeight}px), transparent 100%` : null,
  ].filter(Boolean);
  const mask = stops.length
    ? `linear-gradient(to bottom, ${stops.join(", ")})`
    : null;

  return {
    ref,
    /** True while content remains below the fold. Back-compat alias of `showBottomFade`. */
    showFade: fade.bottom,
    /** True while content remains above the fold (only ever set when `edges` includes top). */
    showTopFade: fade.top,
    /** True while content remains below the fold. */
    showBottomFade: fade.bottom,
    /** Spread into the scroll container's `sx`. */
    fadeSx: {
      transition: "mask-image .15s, -webkit-mask-image .15s",
      ...(mask ? { maskImage: mask, WebkitMaskImage: mask } : {}),
    } as const,
    /** Escape hatch for callers composing their own sx conditionally. */
    remeasure: measure,
  };
};
